import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuditLogTable } from '../../../components/AuditLogTable.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { UserPicker } from '../../../components/UserPicker.js';
import { MemberLabel } from '../../../components/MemberLabel.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';
import {
  useAuditLogs,
  useChannels,
  useConfigAudit,
  useGuildConfig,
  useGuildPatch,
  type AuditFilters,
  type AuditLogEntry,
  type ConfigAuditEntry,
} from '../../../lib/auditQueries.js';

export const Route = createFileRoute('/guilds/$guildId/audit')({
  component: AuditPage,
});

type Tab = 'discord' | 'config';

function AuditPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const [tab, setTab] = useState<Tab>('discord');
  const [filters, setFilters] = useState<AuditFilters>({});
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<ConfigAuditEntry | null>(null);

  const guildConfig = useGuildConfig(guildId);
  const guildPatch = useGuildPatch(guildId);
  const channelsQuery = useChannels(guildId);
  const audit = useAuditLogs(guildId, filters);
  const configAudit = useConfigAudit(guildId);

  const channelNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of channelsQuery.data ?? []) m.set(c.id, c.name);
    return m;
  }, [channelsQuery.data]);

  const rows = audit.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <Link to="/" className="shrink-0">
            <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
          </Link>
          <Breadcrumbs
            items={[
              { label: t('app.name'), to: '/' },
              {
                label: guildConfig.data?.name ?? guildId,
                to: '/guilds/$guildId',
                params: { guildId },
              },
              { label: t('audit.title') },
            ]}
          />
          <div className="ml-auto">
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-12 gap-6 px-6 py-8">
        <aside className="col-span-12 space-y-6 lg:col-span-3">
          <Section title={t('audit.section.recording')}>
            {guildConfig.data ? (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                  checked={guildConfig.data.auditLogEnabled}
                  onChange={(e) => {
                    guildPatch.mutate({ auditLogEnabled: e.target.checked });
                  }}
                />
                <span className="text-sm">
                  {t('audit.toggle.label')}
                  <span className="mt-1 block text-xs text-amber-300/80">
                    {t('audit.toggle.warning')}
                  </span>
                </span>
              </label>
            ) : (
              <div className="text-sm text-zinc-400">{t('common.loading')}</div>
            )}
          </Section>

          <Section title={t('audit.section.filter')}>
            <FilterField label={t('audit.filter.channel')}>
              <ChannelPicker
                guildId={guildId}
                value={filters.channelId ?? ''}
                onChange={(id) => {
                  setFilters((f) => ({ ...f, channelId: id || undefined }));
                }}
                placeholder={t('audit.filter.channelAll')}
              />
            </FilterField>
            <FilterField label={t('audit.filter.authorId')}>
              <UserPicker
                guildId={guildId}
                value={filters.authorId ?? ''}
                onChange={(id) => {
                  setFilters((f) => ({ ...f, authorId: id || undefined }));
                }}
              />
            </FilterField>
            <FilterField label={t('audit.filter.event')}>
              <select
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={filters.eventType ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({
                    ...f,
                    eventType: (e.target.value || undefined) as AuditFilters['eventType'],
                  }));
                }}
              >
                <option value="">{t('audit.filter.eventAll')}</option>
                <option value="message_edit">{t('audit.event.edit')}</option>
                <option value="message_delete">{t('audit.event.delete')}</option>
                <option value="message_bulk_delete">{t('audit.event.bulkDelete')}</option>
              </select>
            </FilterField>
            <FilterField label={t('audit.filter.from')}>
              <input
                type="datetime-local"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={filters.from ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, from: e.target.value || undefined }));
                }}
              />
            </FilterField>
            <FilterField label={t('audit.filter.to')}>
              <input
                type="datetime-local"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={filters.to ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, to: e.target.value || undefined }));
                }}
              />
            </FilterField>
            <button
              type="button"
              onClick={() => {
                setFilters({});
              }}
              className="mt-2 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
            >
              {t('audit.filter.reset')}
            </button>
          </Section>
        </aside>

        <section className="col-span-12 lg:col-span-9">
          <div className="mb-4 flex gap-1 rounded-md border border-zinc-800 bg-zinc-900/40 p-1">
            {(['discord', 'config'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setTab(k);
                }}
                className={`flex-1 rounded px-3 py-1.5 text-sm transition-colors ${
                  tab === k ? 'bg-indigo-600 text-white' : 'text-zinc-300 hover:bg-zinc-800/60'
                }`}
              >
                {t(`audit.tab.${k}`)}
              </button>
            ))}
          </div>

          {tab === 'discord' ? (
            audit.isLoading ? (
              <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
                {t('common.loading')}
              </div>
            ) : rows.length === 0 ? (
              <EmptyState enabled={guildConfig.data?.auditLogEnabled ?? false} />
            ) : (
              <>
                <AuditLogTable
                  guildId={guildId}
                  rows={rows}
                  onSelect={setSelected}
                  channelNames={channelNames}
                />
                <div className="mt-4 flex justify-center">
                  {audit.hasNextPage ? (
                    <button
                      type="button"
                      disabled={audit.isFetchingNextPage}
                      onClick={() => {
                        void audit.fetchNextPage();
                      }}
                      className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-50"
                    >
                      {audit.isFetchingNextPage ? t('common.loadingMore') : t('common.loadMore')}
                    </button>
                  ) : (
                    <span className="text-xs text-zinc-500">{t('common.endOfList')}</span>
                  )}
                </div>
              </>
            )
          ) : (
            <ConfigAuditView guildId={guildId} query={configAudit} onSelect={setSelectedConfig} />
          )}
        </section>
      </main>

      {selected ? (
        <DetailModal
          guildId={guildId}
          entry={selected}
          channelName={channelNames.get(selected.channelId)}
          onClose={() => {
            setSelected(null);
          }}
        />
      ) : null}
      {selectedConfig ? (
        <ConfigDiffModal
          guildId={guildId}
          entry={selectedConfig}
          onClose={() => {
            setSelectedConfig(null);
          }}
        />
      ) : null}
    </div>
  );
}

interface ConfigAuditQuery {
  data: { pages: { items: ConfigAuditEntry[]; nextCursor: string | null }[] } | undefined;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  fetchNextPage: () => Promise<unknown>;
}

function ConfigAuditView({
  guildId,
  query,
  onSelect,
}: {
  guildId: string;
  query: ConfigAuditQuery;
  onSelect: (entry: ConfigAuditEntry) => void;
}) {
  const { t } = useTranslation();
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  if (query.isLoading) {
    return (
      <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
        {t('common.loading')}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
        {t('audit.config.empty')}
      </div>
    );
  }
  return (
    <>
      <table className="w-full overflow-hidden rounded-md border border-zinc-800 text-sm">
        <thead className="bg-zinc-900/60 text-xs uppercase tracking-wider text-zinc-400">
          <tr>
            <th className="px-3 py-2 text-left">{t('audit.config.date')}</th>
            <th className="px-3 py-2 text-left">{t('audit.config.actor')}</th>
            <th className="px-3 py-2 text-left">{t('audit.config.action')}</th>
            <th className="px-3 py-2 text-left">{t('audit.config.target')}</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="cursor-pointer border-t border-zinc-800 hover:bg-zinc-900/40"
              onClick={() => {
                onSelect(r);
              }}
            >
              <td className="px-3 py-2 text-xs text-zinc-400">
                {new Date(r.occurredAt).toLocaleString()}
              </td>
              <td className="px-3 py-2">
                <MemberLabel guildId={guildId} userId={r.actorUserId} compact />
              </td>
              <td className="px-3 py-2">{r.action}</td>
              <td className="px-3 py-2 text-xs text-zinc-400">
                {r.targetType}
                {r.targetId ? ` · ${r.targetId.slice(0, 8)}` : ''}
              </td>
              <td className="px-3 py-2 text-right text-xs text-indigo-300">
                {t('audit.config.viewDiff')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex justify-center">
        {query.hasNextPage ? (
          <button
            type="button"
            disabled={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-50"
          >
            {query.isFetchingNextPage ? t('common.loadingMore') : t('common.loadMore')}
          </button>
        ) : (
          <span className="text-xs text-zinc-500">{t('common.endOfList')}</span>
        )}
      </div>
    </>
  );
}

function ConfigDiffModal({
  guildId,
  entry,
  onClose,
}: {
  guildId: string;
  entry: ConfigAuditEntry;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 p-5 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">{entry.action}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.dismiss')}
          </button>
        </header>
        <dl className="mb-3 grid grid-cols-2 gap-2 text-xs">
          <dt className="text-zinc-400">{t('audit.config.date')}</dt>
          <dd>{new Date(entry.occurredAt).toLocaleString()}</dd>
          <dt className="text-zinc-400">{t('audit.config.actor')}</dt>
          <dd>
            <MemberLabel guildId={guildId} userId={entry.actorUserId} compact />
          </dd>
          <dt className="text-zinc-400">{t('audit.config.target')}</dt>
          <dd className="font-mono">
            {entry.targetType}
            {entry.targetId ? ` / ${entry.targetId}` : ''}
          </dd>
        </dl>
        <div className="grid grid-cols-2 gap-3">
          <DiffSide label={t('audit.config.before')} value={entry.diff?.before ?? null} />
          <DiffSide label={t('audit.config.after')} value={entry.diff?.after ?? null} />
        </div>
      </div>
    </div>
  );
}

function DiffSide({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-zinc-400">{label}</h3>
      <pre className="max-h-96 overflow-auto rounded-md border border-zinc-800 bg-zinc-900 p-3 text-xs">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">{title}</h3>
      <div className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
        {children}
      </div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-zinc-400">{label}</span>
      {children}
    </label>
  );
}

function EmptyState({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
      {enabled ? t('audit.empty.noEvents') : t('audit.empty.disabled')}
    </div>
  );
}

function DetailModal({
  guildId,
  entry,
  channelName,
  onClose,
}: {
  guildId: string;
  entry: AuditLogEntry;
  channelName?: string | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h2 className="text-base font-semibold">{entry.eventType}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            {t('audit.detail.close')}
          </button>
        </div>
        <dl className="grid grid-cols-3 gap-x-6 gap-y-3 px-6 py-4 text-sm">
          <Field label={t('audit.detail.date')}>
            {new Date(entry.occurredAt).toLocaleString()}
          </Field>
          <Field label={t('audit.detail.channel')}>#{channelName ?? entry.channelId}</Field>
          <Field label={t('audit.detail.messageId')}>
            <code className="font-mono text-xs text-zinc-400">{entry.messageId ?? '—'}</code>
          </Field>
          <Field label={t('audit.detail.author')}>
            {entry.authorId ? (
              <MemberLabel guildId={guildId} userId={entry.authorId} />
            ) : (
              (entry.authorTag ?? '—')
            )}
          </Field>
          <Field label={t('audit.detail.authorId')}>
            <code className="font-mono text-xs text-zinc-400">{entry.authorId ?? '—'}</code>
          </Field>
          <Field label={t('audit.detail.attachments')}>{String(entry.attachments.length)}</Field>
        </dl>
        {entry.contentBefore !== null ? (
          <ContentBlock label={t('audit.detail.before')} content={entry.contentBefore} />
        ) : null}
        {entry.contentAfter !== null ? (
          <ContentBlock label={t('audit.detail.after')} content={entry.contentAfter} />
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function ContentBlock({ label, content }: { label: string; content: string }) {
  // SPEC §16: never dangerouslySetInnerHTML for Discord-sourced content. Plain text only.
  return (
    <div className="border-t border-zinc-800 px-6 py-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">{label}</h3>
      <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-zinc-800 bg-zinc-900/60 p-3 text-sm text-zinc-200">
        {content}
      </pre>
    </div>
  );
}
